<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch} from "vue";
import {PageLoading} from "@/Components/Global";
import {Button, Checkbox, Dialog, Drawer, Icon, Menu, ToggleSwitch} from "@/Components/Library";
import GroupQuestionCard from "@/Pages/User/Feed/GroupQuestionCard.vue";
import {
    deleteGroupQuestion,
    deleteMultipleGroupQuestions,
    getGroupQuestions,
    reorderGroupQuestion,
    setGroupQuestionsOrder,
    togglePinGroupQuestion,
    updateParams,
} from "@/Services/group-questions.ts";
import {useMainStore} from "@/Stores/store.ts";
import {useRouter, useRoute} from "vue-router";
import GroupQuestionPublishDatesDialog from "@/Pages/User/Groups/GroupQuestionPublishDatesDialog.vue";
import {useToast} from "primevue/usetoast";
import useEcho from "@/Composables/echo.ts";
import {useConfirm} from "primevue/useconfirm";
import User from "@/Models/User.ts";
import GroupFeedFilters from "@/Pages/User/Groups/Group/GroupFeedFilters.vue";
import GroupFeedEvents from "./group-feed-events.ts";
import {exportGroupData} from "@/Services/groups.ts";
import Str from "@/Utils/Str.ts";
import type {Tag} from "@/Types";

const $store = useMainStore();
const $user = new User();
const toast = useToast();
const currentRoute = useRoute();
const router = useRouter();
const confirm = useConfirm();

const props = defineProps<{
    group: any;
}>();
const emit = defineEmits<{
    analyze: [params: {filter: Record<string, unknown>; sort: string[]}];
}>();
// computed flag from server resource
const hasGatekeepers = computed(() => !!props.group?.has_gatekeepers);
const isLinearFlow = computed(() => !!props.group?.params?.linear_flow);
const state = reactive({
    mode: "default" as "default" | "select" | "reorder",
    loading: {
        questions: false,
        exportData: false,
    },
    show: {
        filters: false,
        publishDates: false,
        questionMenu: false,
        changeDeadline: false,
    },
    limits: {
        responses_count: 100,
    },
    selectedQuestions: [] as string[],
});

const questions = ref([]);

const params = reactive({
    page: $store.feed.params.page,
    sort: "order_column",
    filter: {
        group_ids: [],
        "question.title": null,
        type: [],
        stimulus_type: [],
        responded: false,
        unanswered: false,
        commented: false,
        responses_count: [0, 100],
        created_at: null,
        publish_from: null,
        publish_to: null,
        currently_published: null,
        ownership: "all",
        tags: [],
        strict_tags: false,
    },
});

const filters = ref({
    type: {
        post: false,
        word: false,
        text: false,
        multiple_choice: false,
        ranking: false,
        slider: false,
        weighting: false,
        annotation: false,
        voice: false,
        gatekeeper: false,
    },
    responses_count: [0, 100] as [number, number],
    tags: [] as Tag[],
    strict_tags: false,
    created_at: null,
    publish_from: null,
    publish_to: null,
    currently_published: null as "active" | "inactive" | null,
    state: "created" as "created" | "archived",
    ownership: "all" as "all" | "mine",
    unanswered: false,
});

const selectedTagNames = computed(() => {
    return filters.value.tags
        .map((tag) => (typeof tag === "string" ? tag : tag?.name))
        .filter((tag): tag is string => !!tag);
});
const sort = ref(["order_column", "desc"]);

// enforce default sort if group has gatekeepers
if (hasGatekeepers.value) {
    sort.value = ["order_column", "desc"];
}

// react if flag arrives later
watch(
    () => props.group?.has_gatekeepers,
    (val) => {
        if (val) {
            sort.value = ["order_column", "desc"];
            state.show.filters = false;
        }
    },
);

const unansweredCount = computed(() => {
    return Math.max(totalQuestionsCount.value - answeredCount.value, 0);
});

const currentMember = computed(() => {
    return props.group?.members?.find((member) => member?.id === $store.user?.id);
});

const answeredCount = computed(() => {
    return Math.min(totalQuestionsCount.value, Math.max(0, +(currentMember.value?.answered_count ?? 0)));
});

const totalQuestionsCount = computed(() => {
    return Math.max(0, +(props.group?.questions_count ?? 0));
});

const progressPercent = computed(() => {
    if (!totalQuestionsCount.value) {
        return 100;
    }

    return Math.min(100, Math.round((answeredCount.value / totalQuestionsCount.value) * 100));
});

const progressState = computed(() => {
    if (!totalQuestionsCount.value) {
        return "green";
    }

    if (!answeredCount.value) {
        return "red";
    }

    if (answeredCount.value >= totalQuestionsCount.value) {
        return "green";
    }

    return "yellow";
});

const trafficTextClass = computed(() => {
    return {
        red: "text-red-600",
        yellow: "text-amber-600",
        green: "text-green-600",
    }[progressState.value];
});

const trafficBadgeClass = computed(() => {
    return {
        red: "bg-red-600 text-white",
        yellow: "bg-amber-600 text-white",
        green: "bg-green-600 text-white",
    }[progressState.value];
});

const trafficBarClass = computed(() => {
    return {
        red: "bg-red-600",
        yellow: "bg-amber-600",
        green: "bg-green-600",
    }[progressState.value];
});

const getQuestions = () => {
    state.loading.questions = true;
    getGroupQuestions(currentRoute.params.group, {filter: params.filter, sort: sort.value}).then(({data, meta}) => {
        console.log("Qs", data);
        state.limits.responses_count = meta.max_responses_count;
        if (filters.value.responses_count[1] > state.limits.responses_count) {
            filters.value.responses_count[1] = meta.max_responses_count;
        }
        $store.group.questions = data;
        questions.value = data;
        state.loading.questions = false;
    });
};
if (!props.group?.questions?.length) {
    getQuestions();
} else {
    $store.group.questions = props.group.questions;
}

const sortedQuestions = computed(() => {
    console.log($store.group.questions);
    if (state.mode === "reorder") {
        return [...$store.group.questions].sort((a, b) => b.order_column - a.order_column);
    }
    return $store.group.questions;
});

const reorderQuestion = async (id: string, index: number, direction: "up" | "down" | "start" | "end") => {
    reorderGroupQuestion(props.group.id, id, {direction: direction}).then();

    if (direction === "start" || direction === "end") {
        // For start/end, move to first or last position
        const targetIndex = direction === "start" ? 0 : $store.group.questions.length - 1;
        const movedQuestion = sortedQuestions.value[index];
        const newQuestions = [...sortedQuestions.value];
        newQuestions.splice(index, 1);
        newQuestions.splice(targetIndex, 0, movedQuestion);

        $store.group.questions = newQuestions.map((Q, i) => {
            Q.order_column = newQuestions.length - i;
            return Q;
        });
    } else {
        // Original up/down logic
        const newIndex = direction === "up" ? index - 1 : index + 1;

        $store.group.questions = sortedQuestions.value.map((Q, i) => {
            if (i === index) {
                Q.order_column = direction === "up" ? Q.order_column + 1 : Q.order_column - 1;
            } else if (i === newIndex) {
                Q.order_column = direction === "up" ? Q.order_column - 1 : Q.order_column + 1;
            }
            return Q;
        });
    }
};

const sortPanel = useTemplateRef("sortPanel");
const sortMenuItems = ref([
    {
        label: "Most recent",
        icon: "material-symbols:date-range",
        command: () => {
            if ((hasGatekeepers.value || isLinearFlow.value) && !props.group.can_admin) return;
            // console.log(sort.value);
            sort.value = ["created_at", "desc"];
        },
    },
    {
        label: "Most urgent",
        icon: "mdi:clipboard-text-date",
        command: () => {
            if ((hasGatekeepers.value || isLinearFlow.value) && !props.group.can_admin) return;
            sort.value = ["publish_to", "asc"];
        },
    },
    {
        label: "Alphabetical",
        icon: "mdi:sort-alphabetical-ascending",
        command: () => {
            if ((hasGatekeepers.value || isLinearFlow.value) && !props.group.can_admin) return;
            sort.value = ["title", "asc"];
        },
    },
    {
        label: "Default",
        icon: "lucide:list-ordered",
        command: () => {
            if ((hasGatekeepers.value || isLinearFlow.value) && !props.group.can_admin) return;
            sort.value = ["order_column", "desc"];
        },
    },
]);

const totalActiveFilters = computed(() => {
    let count = 0;
    if (Object.values(filters.value.type).some((f) => f)) {
        count++;
    }

    // if (filters.value.responses_count[0] > 0 || filters.value.responses_count[1] < state.limits.responses_count) {
    //     count++;
    // }

    if (filters.value.created_at) {
        count++;
    }

    if (filters.value.publish_from) {
        count++;
    }

    if (filters.value.publish_to) {
        count++;
    }

    if (filters.value.currently_published) {
        count++;
    }

    if (filters.value.state !== "created") {
        count++;
    }

    if (filters.value.ownership && filters.value.ownership !== "all") {
        count++;
    }

    if (filters.value.tags.length > 0) {
        count++;

        if (filters.value.strict_tags) {
            count++;
        }
    }

    if (filters.value.unanswered) {
        count++;
    }

    return count;
});

const applyFilters = () => {
    const newParams = {
        page: 0,
        filter: {
            ...params.filter,
            type: Object.keys(filters.value.type).filter(
                (key) => filters.value.type[key as keyof typeof filters.value.type],
            ),
            responses_count: [...filters.value.responses_count],
            created_at: filters.value.created_at,
            publish_from: filters.value.publish_from,
            publish_to: filters.value.publish_to,
            currently_published: filters.value.currently_published ?? null,
            state: filters.value.state,
            ownership: filters.value.ownership,
            tags: selectedTagNames.value,
            strict_tags: filters.value.strict_tags,
            unanswered: filters.value.unanswered,
        },
    };
    Object.assign(params, newParams);
    state.selectedQuestions = [];
    state.show.filters = false;
};

const analyzeCurrentFeed = () => {
    const selectedQuestionIds =
        state.mode === "select"
            ? props.group.questions
                  .filter((q) => state.selectedQuestions.includes(q.group_question_id))
                  .map((q) => q.id)
            : [];

    const filter = JSON.parse(JSON.stringify(params.filter));
    if (selectedQuestionIds.length) {
        filter.question_ids = selectedQuestionIds;
    }

    emit("analyze", {
        filter: filter,
        sort: [...sort.value],
    });
};

const clearFilters = () => {
    Object.assign(filters.value, {
        type: {
            post: false,
            word: false,
            text: false,
            multiple_choice: false,
            ranking: false,
            slider: false,
            weighting: false,
            annotation: false,
            voice: false,
        },
        responses_count: [0, 100],
        tags: [] as Tag[],
        strict_tags: false,
        created_at: null,
        publish_from: null,
        publish_to: null,
        currently_published: null as "active" | "inactive" | null,
        state: "created" as "created" | "archived",
        ownership: "all" as "all" | "mine",
        unanswered: false,
    });
    applyFilters();
};

const selectedQuestion = ref();

const togglePin = async () => {
    try {
        const wasPinned = selectedQuestion.value?.is_pinned;
        await togglePinGroupQuestion(props.group.id, selectedQuestion.value?.id);

        selectedQuestion.value.is_pinned = !wasPinned;

        if (selectedQuestion.value.is_pinned) {
            const pinnedQuestion = props.group.questions.find((q) => q.id !== selectedQuestion.value.id && q.is_pinned);
            if (pinnedQuestion) {
                pinnedQuestion.is_pinned = false;
            }
        }

        getQuestions();

        state.show.questionMenu = false;

        toast.add({
            severity: "success",
            summary: !wasPinned ? "Post pinned" : "Post unpinned",
            detail: !wasPinned ? "This post will appear at the top of the feed." : "This post has been unpinned.",
            life: 3000,
        });
    } catch (error) {
        console.error(error);
    }
};

const questionMenuItemsBase = computed(() => [
    {
        label: selectedQuestion.value?.is_pinned ? "Unpin" : "Pin",
        icon: selectedQuestion.value?.is_pinned ? "mdi:pin-off" : "mdi:pin",
        command: () => togglePin(),
    },
    {
        label: "Update publish dates",
        icon: "prime:calendar",
        command: async () => {
            state.show.questionMenu = false;
            state.show.publishDates = true;
        },
    },
    {
        label: "Remove from this group",
        icon: "prime:trash",
        command: () => {
            // console.log("selectedQuestion", selectedQuestion.value);
            confirm.require({
                header: "Remove question",
                message: "Are you sure you want to remove this question from the group?",
                acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
                rejectClass: "px-3 py-1.5",
                accept: async () => {
                    await deleteGroupQuestion(props.group.id, selectedQuestion.value?.id);
                    props.group.questions = props.group.questions.filter((q) => q.id !== selectedQuestion.value?.id);
                    state.show.questionMenu = false;
                    toast.add({
                        severity: "success",
                        summary: "Question removed",
                        detail: "The question has been removed from the group.",
                        life: 3000,
                    });
                },
            });
        },
        class: "text-red-600 hover:bg-red-100",
    },
]);

const questionMenuItems = ref([
    {
        label: "Post",
        items: [] as typeof questionMenuItemsBase,
    },
]);

const toggleQuestionMenu = (question) => {
    // console.log(question);
    selectedQuestion.value = question;
    // Create a fresh copy of the base items to avoid duplicates
    questionMenuItems.value[0].items = [...questionMenuItemsBase.value];
    state.show.questionMenu = true;
    questionMenuItems.value[0].label = Str.of(selectedQuestion.value?.title ?? "Post").truncate().value;
    if ($user.hasRole("super-admin")) {
        questionMenuItems.value[0].items.push({
            label: "Edit",
            icon: "mdi:pencil",
            class: "",
            command: () => {
                state.show.questionMenu = false;
                // console.log(currentRoute);
                router.push({
                    name: "questions.edit",
                    params: {question: selectedQuestion.value?.id},
                    query: {group: props.group.id},
                });
            },
        });
    }
};

const saveQuestionParam = async (key: string, label: string, newValue: boolean, questionId?: string) => {
    const targetQuestionId = questionId || selectedQuestion.value.id;
    const targetQuestion = questionId ? props.group.questions.find((q) => q.id === questionId) : selectedQuestion.value;
    if (!targetQuestion) {
        console.error("Target question not found for param update");
        return;
    }

    try {
        targetQuestion.params[key] = newValue;
        await updateParams(props.group.id, targetQuestionId, {[key]: newValue});

        // Update the question in the store
        const storeQuestion = $store.group.questions.find((q) => q.id === targetQuestionId);
        if (storeQuestion) {
            storeQuestion.params[key] = newValue;
        }

        toast.add({
            severity: "success",
            summary: "Settings Updated",
            detail: `${label} have been ${newValue ? "enabled" : "disabled"}`,
            life: 3000,
        });
    } catch (e) {
        console.error(e);
        toast.add({
            severity: "error",
            summary: "Error",
            detail: `Failed to update ${label}`,
            life: 3000,
        });
    }
};

const showCharts = computed({
    get: () => !!selectedQuestion.value?.params?.show_charts,
    set: (value: boolean) => saveQuestionParam("show_charts", "Charts", value),
});

const showResponses = computed({
    get: () => !!selectedQuestion.value?.params?.show_responses,
    set: (value: boolean) => saveQuestionParam("show_responses", "Responses", value),
});

const allowComments = computed({
    get: () => !!selectedQuestion.value?.params?.allow_comments,
    set: (value: boolean) => saveQuestionParam("allow_comments", "Comments", value),
});

const toggleLabels = {
    show_charts: "Charts",
    show_responses: "Responses",
    allow_comments: "Comments",
    allow_multiple: "Multiple Responses",
} as const;

const toggleQuestionParam = (key: keyof typeof toggleLabels, question: any) => {
    saveQuestionParam(
        key,
        key === "allow_multiple" && question.params.matrix ? "Multiple Categories" : toggleLabels[key],
        !question.params[key],
        question.id,
    );
};

const toggleMode = async (mode: "select" | "reorder") => {
    if (state.mode === mode) {
        state.mode = "default";
    } else {
        // When entering reorder mode, save current order as default
        if (mode === "reorder") {
            try {
                await setGroupQuestionsOrder(props.group.id, {
                    order: sortedQuestions.value.map((q) => q.group_question_id),
                });
                // Set sort to order_column to maintain the saved order
                sort.value = ["order_column", "desc"];
                toast.add({
                    severity: "success",
                    summary: "Current order saved",
                    detail: "The current order has been saved as the default order.",
                    life: 3000,
                });
            } catch (error) {
                console.error(error);
                toast.add({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to save current order.",
                    life: 3000,
                });
                return; // Don't enter reorder mode if save fails
            }
        }
        state.mode = mode;
    }
};

const massArchive = () => {
    if (!state.selectedQuestions.length) {
        alert("Please select at least one post to archive.");
        return;
    }
    confirm.require({
        header: "Archive selected posts",
        message: `Are you sure you want to archive ${state.selectedQuestions.length} posts from this group?`,
        acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: async () => {
            try {
                await deleteMultipleGroupQuestions(props.group.id, {group_questions: state.selectedQuestions});
                props.group.questions = props.group.questions.filter(
                    (q) => !state.selectedQuestions.includes(q.group_question_id),
                );
                $store.group.questions = props.group.questions;
                state.selectedQuestions = [];
                state.mode = "default";
                toast.add({
                    severity: "success",
                    summary: "Posts removed",
                    detail: "The selected posts have been removed from the group.",
                    life: 3000,
                });
            } catch (error) {
                console.error(error);
            }
        },
    });
};

const bulkMinPublishTo = computed(() => {
    if (!state.selectedQuestions.length) {
        return null;
    }
    const selectedQs = props.group.questions.filter((q) => state.selectedQuestions.includes(q.group_question_id));
    const dates = selectedQs.map((q) => q.publish_from).filter(Boolean);
    if (!dates.length) {
        return null;
    }
    return dates.reduce((latest, d) => (new Date(d) > new Date(latest) ? d : latest));
});

const onBulkExtendSaved = ({publish_to}: {publish_to: string}) => {
    props.group.questions = props.group.questions.map((q) => {
        if (state.selectedQuestions.includes(q.group_question_id)) {
            return {...q, publish_to};
        }
        return q;
    });
    $store.group.questions = props.group.questions;
    state.selectedQuestions = [];
    state.mode = "default";
    toast.add({
        severity: "success",
        summary: "Deadlines changed",
        detail: "The publish dates have been updated for the selected posts.",
        life: 3000,
    });
};

const saveDefaultOrder = () => {
    if (state.mode === "reorder") {
        toggleMode("reorder");
        return;
    }
    confirm.require({
        header: "Save default order",
        message: "Are you sure you want to save the current order as the default order?",
        acceptClass: "bg-askr-green px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: async () => {
            try {
                await setGroupQuestionsOrder(props.group.id, {
                    order: sortedQuestions.value.map((q) => q.group_question_id),
                });
                sort.value = ["order_column", "desc"];
                toast.add({
                    severity: "success",
                    summary: "Default order saved",
                    detail: "The current order has been saved as the default order.",
                    life: 3000,
                });
            } catch (error) {
                console.error(error);
            }
        },
    });
};

const exportData = async () => {
    try {
        if (!state.selectedQuestions.length) {
            alert("Please select at least one post to export.");
            return;
        }
        state.loading.exportData = true;
        const questionIds = props.group.questions
            .filter((q) => state.selectedQuestions.includes(q.group_question_id))
            .map((q) => q.id);
        const {path} = await exportGroupData(props.group.id, {question_ids: questionIds, order: sort.value});
        // window.location.href = route("files.download", path);
        window.open(route("files.download", path), "_blank");
        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Group data exported successfully.",
            life: 3000,
        });
    } catch (error) {
        console.error(error);
    } finally {
        state.loading.exportData = false;
    }
};

const showChangeDeadline = () => {
    if (!state.selectedQuestions.length) {
        alert("Please select at least one post to change.");
        return;
    }

    state.show.changeDeadline = true;
};

watch([params, () => sort.value], getQuestions);

useEcho(GroupFeedEvents(props, $store, state, sort, params.filter, getQuestions));
</script>
<template>
    <div>
        <div class="mb-2 flex items-center" v-auto-animate>
            <h3 class="mr-2 font-bold">Posts</h3>
            <div v-if="state.mode !== 'select'" class="mr-2 flex items-center gap-1">
                <span
                    class="text--xs min-w-8 rounded-full px-2 py-1 text-center font-semibold"
                    :class="trafficBadgeClass"
                    v-tooltip.left="'Unanswered questions'"
                >
                    {{ answeredCount }} / {{ totalQuestionsCount }}
                </span>
                <Button
                    v-tooltip.right="filters.unanswered ? 'Show all' : 'Show only unanswered'"
                    :icon="{
                        icon: filters.unanswered ? 'mdi:filter-off' : 'mdi:filter',
                        width: '20',
                    }"
                    :class="
                        filters.unanswered
                            ? `${trafficBarClass} text-white`
                            : 'bg-stone-200 text-stone-600 dark:bg-stone-700 dark:text-stone-400'
                    "
                    class="h-8 w-8 flex-none"
                    @click="
                        () => {
                            filters.unanswered = !filters.unanswered;
                            applyFilters();
                        }
                    "
                />
            </div>
            <div v-if="state.mode === 'select' && group.can_admin" class="flex grow items-center">
                <div class="mr-2 flex items-center gap-1.5">
                    <span
                        class="min-w-8 rounded-full px-2 py-1 text-center font-semibold"
                        :class="[
                            state.selectedQuestions.length
                                ? 'bg-askr-green text-white'
                                : 'bg-stone-200 dark:bg-stone-700',
                        ]"
                    >
                        {{ state.selectedQuestions.length }}
                    </span>
                    <span class="text-sm">Selected</span>
                </div>
                <Button
                    v-tooltip.right="'Select all'"
                    :icon="{icon: 'material-symbols:select-all', width: 24}"
                    class="h-8 w-6 flex-none"
                    :class="
                        state.selectedQuestions.length === group.questions.length ? 'text-askr-green' : 'text-stone-300'
                    "
                    @click="state.selectedQuestions = group.questions.map((q) => q.group_question_id)"
                />
                <Button
                    v-tooltip.right="'Deselect all'"
                    :icon="{icon: 'simple-line-icons:minus', width: 18}"
                    class="mr-auto h-8 w-6 flex-none"
                    :class="!state.selectedQuestions.length ? 'text-red-600' : 'text-stone-300'"
                    @click="state.selectedQuestions = []"
                />
                <Button
                    v-if="group.can_admin"
                    v-tooltip.left="
                        state.selectedQuestions.length ? 'Analyze selected posts' : 'Analyze filtered posts'
                    "
                    :icon="{icon: 'ri:robot-2-fill'}"
                    class="ml-2 bg-askr-green !px-3 !py-1 text-xs text-white hover:brightness-110"
                    @click="analyzeCurrentFeed"
                >
                    Analyze
                </Button>
                <Button
                    v-if="$user.can('export data') || group.can_admin"
                    v-tooltip.left="'Export selected posts'"
                    :icon="{icon: 'prime:download', width: '24'}"
                    class="ml-2 bg-askr-green !px-3 !py-1 text-xs text-white hover:brightness-110"
                    :loading="state.loading.exportData"
                    @click="exportData"
                >
                    Export
                </Button>
                <Button
                    v-tooltip.left="'Extend deadline'"
                    :icon="{icon: 'prime:calendar', width: '24'}"
                    class="ml-2 bg-askr-green !px-3 !py-1 text-xs text-white hover:brightness-110"
                    @click="showChangeDeadline"
                >
                    Change deadline
                </Button>
                <Button
                    v-tooltip.left="'Archive selected posts'"
                    :icon="{icon: 'prime:trash', width: '24'}"
                    class="ml-2 bg-red-600 !px-3 !py-1 text-xs text-white hover:brightness-110"
                    @click="massArchive"
                >
                    Archive
                </Button>
            </div>
            <div v-if="state.mode === 'select'" class="ml-2">
                <Button
                    v-tooltip.left="'Exit select mode'"
                    :icon="{icon: 'mdi:close', width: '24'}"
                    class="ml-2 ml-auto h-10 w-10 flex-none text-red-600 hover:brightness-110"
                    @click="toggleMode(state.mode)"
                />
            </div>
            <div v-else class="ml-auto flex items-center">
                <Button
                    v-if="group.can_admin"
                    v-tooltip.left="'Analyze with AI'"
                    :icon="{icon: 'ri:robot-2-fill'}"
                    class="pointer-events-auto ml-auto h-10 w-10"
                    @click="analyzeCurrentFeed"
                />
                <Button
                    v-if="group.can_admin"
                    v-tooltip.left="'Select posts'"
                    :icon="{icon: 'material-symbols:checklist', width: '24'}"
                    class="pointer-events-auto ml-auto h-10 w-10"
                    @click="toggleMode('select')"
                />
                <Button
                    v-if="group.can_admin && !group.params.allow_member_posts"
                    v-tooltip.left="'Reorder posts'"
                    :icon="{icon: 'ic:round-swap-vert', width: '24'}"
                    rounded
                    class="pointer-events-auto ml-auto h-10 w-10"
                    @click="toggleMode('reorder')"
                />
                <Button
                    v-if="group.can_admin && !group.params.allow_member_posts"
                    v-tooltip.left="'Save as default order'"
                    :icon="{icon: 'ic:check', width: '24'}"
                    rounded
                    class="pointer-events-auto ml-auto h-10 w-10"
                    @click="saveDefaultOrder"
                />
                <Button
                    v-if="group.can_admin || (!group.params.allow_member_posts && !hasGatekeepers && !isLinearFlow)"
                    :icon="{icon: 'iconoir:sort', width: 22}"
                    :rounded="false"
                    class="aspect-square h-10 w-10 flex-none bg-askr-green text-white hover:brightness-110"
                    :disabled="state.mode === 'reorder' || (hasGatekeepers && !group.can_admin)"
                    @click="sortPanel.menu.toggle($event)"
                />
                <Menu ref="sortPanel" :popup="true" :model="sortMenuItems" />
                <!-- TODO: make number active filters a badge -->
                <Button
                    v-if="group.can_admin"
                    class="ml-1 rounded-md bg-askr-green !px-2 py-2 text-center font-bold text-white"
                    :icon="{
                        icon: 'hugeicons:filter',
                        width: '24',
                    }"
                    :disabled="hasGatekeepers && !group.can_admin"
                    @click="state.show.filters = true"
                >
                    {{ totalActiveFilters ? totalActiveFilters : null }}
                </Button>
            </div>
        </div>
        <div v-if="state.mode !== 'select'" class="mb-3">
            <div class="h-1.5 w-full overflow-hidden rounded-full bg-stone-200 dark:bg-stone-700">
                <div
                    class="h-full transition-all duration-200"
                    :class="trafficBarClass"
                    :style="{width: `${progressPercent}%`}"
                />
            </div>
        </div>
        <PageLoading v-if="state.loading.questions" class="pt-16" />
        <div v-else-if="!!group.questions?.length" class="pb-20" v-auto-animate>
            <template v-for="(question, index) in sortedQuestions" :key="question.id">
                <div class="flex" v-auto-animate>
                    <div v-auto-animate>
                        <div
                            class="flex flex-col items-center border-t border-stone-300 px-2 pt-3 dark:border-stone-600"
                            v-if="state.mode === 'select' && group.can_admin"
                        >
                            <Checkbox
                                v-model="state.selectedQuestions"
                                :value="question.group_question_id"
                                class="mb-2 scale--150"
                            />
                        </div>
                        <div class="flex flex-col items-center pl-2" v-if="state.mode === 'reorder' && group.can_admin">
                            <Button
                                v-tooltip.top="'Move to top'"
                                :icon="{icon: 'bxs:chevrons-up', width: '20'}"
                                class="!px-3 !py-4"
                                @click.stop="reorderQuestion(question.id, index, 'start')"
                                :disabled="index === 0"
                            />
                            <Button
                                v-tooltip.top="'Move up'"
                                :icon="{icon: 'bxs:up-arrow', width: '20'}"
                                class="!px-3 !py-4"
                                @click.stop="reorderQuestion(question.id, index, 'up')"
                                :disabled="index === 0"
                            />
                            <span
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-askr-green text-lg font-bold text-white"
                            >
                                {{ index + 1 }}
                            </span>
                            <Button
                                v-tooltip.bottom="'Move down'"
                                :icon="{icon: 'bxs:down-arrow', width: '20'}"
                                class="!px-3 !py-4"
                                @click.stop="reorderQuestion(question.id, index, 'down')"
                                :disabled="index === group.questions.length - 1"
                            />
                            <Button
                                v-tooltip.bottom="'Move to bottom'"
                                :icon="{icon: 'bxs:chevrons-down', width: '20'}"
                                class="!px-3 !py-4"
                                @click.stop="reorderQuestion(question.id, index, 'end')"
                                :disabled="index === group.questions.length - 1"
                            />
                        </div>
                    </div>
                    <GroupQuestionCard
                        :question="question"
                        :group="group"
                        :show-menu="group.can_admin"
                        @menu="toggleQuestionMenu(question)"
                        @toggle="toggleQuestionParam"
                    />
                </div>
            </template>
        </div>
        <div v-else class="py-4 text-center">
            {{ !!totalActiveFilters ? "No matching posts found." : "No posts yet." }}
        </div>
        <RouterLink
            v-if="group && (group.can_create || group.params.allow_member_posts)"
            :to="{name: 'groups.questions.create', query: {group: group?.id}}"
            class="fixed bottom-4 right-0 -mt-6 mr-4 lg:-mt-16"
        >
            <button
                class="pointer-events-auto flex h-16 w-16 flex-col items-center justify-center rounded-full bg-askr-green text-white shadow-xl shadow-stone-600 lg:h-24 lg:w-24 dark:shadow-stone-950"
            >
                <Icon icon="mingcute:send-plane-fill" class="h-8 w-8 lg:h-12 lg:w-12" />
                <span class="text-xs font-semibold">LAUNCH</span>
            </button>
        </RouterLink>
        <!--        <FilterBar v-model="state.show.filters" :total-active-filters="totalActiveFilters" :mobile-only="false">-->
        <!--            &lt;!&ndash;            <RouterLink&ndash;&gt;-->
        <!--            &lt;!&ndash;                v-if="group && (group.is_owner || group.is_admin)"&ndash;&gt;-->
        <!--            &lt;!&ndash;                :to="{name: 'questions.index', query: {group: group?.id, launch: true}}"&ndash;&gt;-->
        <!--            &lt;!&ndash;                class="ml-auto mr-4"&ndash;&gt;-->
        <!--            &lt;!&ndash;            >&ndash;&gt;-->
        <!--            &lt;!&ndash;                <Button&ndash;&gt;-->
        <!--            &lt;!&ndash;                    :icon="{icon: 'mdi:plus', width: '36'}"&ndash;&gt;-->
        <!--            &lt;!&ndash;                    rounded&ndash;&gt;-->
        <!--            &lt;!&ndash;                    class="pointer-events-auto h-14 w-14 bg-askr-green text-white shadow-stone-600"&ndash;&gt;-->
        <!--            &lt;!&ndash;                />&ndash;&gt;-->
        <!--            &lt;!&ndash;            </RouterLink>&ndash;&gt;-->
        <!--        </FilterBar>-->
        <Dialog
            v-if="group.can_admin"
            v-model:visible="state.show.questionMenu"
            modal
            dismissable-mask
            class="max-w-sm overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <div class="bg-white dark:bg-stone-800">
                    <Menu :model="questionMenuItems" />
                    <div class="border-t border-stone-200 px-4 py-3 dark:border-stone-700">
                        <h6 class="mb-3 text-sm font-semibold text-stone-600 dark:text-stone-400">Settings</h6>
                        <div class="flex flex-col gap-3">
                            <div class="flex items-center justify-between">
                                <label class="text-sm">Show Charts</label>
                                <ToggleSwitch v-model="showCharts" />
                            </div>
                            <div class="flex items-center justify-between">
                                <label class="text-sm">Show Responses</label>
                                <ToggleSwitch v-model="showResponses" />
                            </div>
                            <div class="flex items-center justify-between">
                                <label class="text-sm">Allow Comments</label>
                                <ToggleSwitch v-model="allowComments" />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Dialog>
        <GroupQuestionPublishDatesDialog
            v-if="group?.id"
            v-model:visible="state.show.publishDates"
            :group-id="group.id"
            :question="selectedQuestion"
        />
        <GroupQuestionPublishDatesDialog
            v-if="group?.id"
            v-model:visible="state.show.changeDeadline"
            :group-id="group.id"
            :group-question-ids="state.selectedQuestions"
            :min-publish-to="bulkMinPublishTo"
            @saved="onBulkExtendSaved"
        />
        <Drawer
            v-model:visible="state.show.filters"
            position="bottom"
            header="Filters"
            class="max-h-dvh w-full max-w-3xl overflow-y-auto"
        >
            <div class="pb-12">
                <GroupFeedFilters
                    v-model="filters"
                    :group="group"
                    :limits="state.limits"
                    :total-active-filters="totalActiveFilters"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
        </Drawer>
    </div>
</template>
<style scoped></style>
